import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AssetKind } from "../../lib/broadcastApi";
import type { AssetLibrary as Library } from "./useAssetLibrary";

interface Props {
  library: Library;
  onAddImageWidget: (assetId: string) => void;
  onUseBackground: (assetId: string) => void;
}

const btn =
  "rounded-md border border-slate-500 px-2 py-1 text-xs hover:bg-slate-800 disabled:opacity-40";

/** Upload + library list for the broadcaster's logo and background images. */
export default function AssetLibrary({
  library,
  onAddImageWidget,
  onUseBackground,
}: Props) {
  const { t } = useTranslation("broadcast");
  const [kind, setKind] = useState<AssetKind>("logo");
  const input = useRef<HTMLInputElement>(null);
  const { assets, error, busy, pending } = library;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    await library.upload(kind, file);
    if (input.current) input.current.value = "";
  };

  const errorKey =
    error?.status === 413 ||
    error?.status === 422 ||
    error?.status === 409 ||
    error?.status === 429
      ? `control.assets.errors.${error.status}`
      : "control.assets.errors.other";
  const onAirUse = pending?.usages.some((u) => u.on_air_program) ?? false;

  return (
    <div
      data-testid="asset-library"
      className="rounded-lg border border-slate-700 p-4 space-y-3"
    >
      <h2 className="text-lg font-semibold">{t("control.assets.title")}</h2>
      <p className="text-xs text-slate-300">{t("control.assets.hint")}</p>

      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label={t("control.assets.kind")}
          data-testid="asset-upload-kind"
          className="rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm"
          value={kind}
          onChange={(e) => setKind(e.target.value as AssetKind)}
        >
          <option value="logo">{t("control.assets.kinds.logo")}</option>
          <option value="background">
            {t("control.assets.kinds.background")}
          </option>
        </select>
        <input
          ref={input}
          type="file"
          accept={kind === "logo" ? "image/png" : "image/png,image/jpeg"}
          disabled={busy}
          aria-label={t("control.assets.upload")}
          data-testid="asset-upload-input"
          className="text-xs"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
      </div>
      <p className="text-xs text-slate-400">
        {kind === "logo"
          ? t("control.assets.logoRule")
          : t("control.assets.backgroundRule")}
      </p>

      {error && (
        <p
          role="alert"
          data-testid="asset-error"
          data-status={error.status ?? ""}
          className="rounded border border-red-500 bg-red-950/40 p-2 text-xs text-red-200"
        >
          {t(errorKey)}
          {error.detail ? ` ${error.detail}` : ""}
        </p>
      )}

      {assets.length === 0 ? (
        <p data-testid="asset-empty" className="text-xs text-slate-400">
          {t("control.assets.empty")}
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-2">
          {assets.map((a) => (
            <li
              key={a.asset_id}
              data-testid={`asset-row-${a.asset_id}`}
              data-kind={a.kind}
              className="rounded-md border border-slate-700 p-2 text-xs"
            >
              <img
                src={a.url}
                alt=""
                className="mb-1 h-16 w-full rounded bg-slate-800 object-contain"
              />
              <div className="text-slate-300">
                {t(`control.assets.kinds.${a.kind}`)} {a.width}x{a.height}
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                <button
                  type="button"
                  className={btn}
                  disabled={busy}
                  data-testid={`asset-add-widget-${a.asset_id}`}
                  onClick={() => onAddImageWidget(a.asset_id)}
                >
                  {t("control.assets.addWidget")}
                </button>
                {a.kind === "background" && (
                  <button
                    type="button"
                    className={btn}
                    disabled={busy}
                    data-testid={`asset-use-bg-${a.asset_id}`}
                    onClick={() => onUseBackground(a.asset_id)}
                  >
                    {t("control.assets.useBackground")}
                  </button>
                )}
                <button
                  type="button"
                  className={`${btn} text-red-300`}
                  disabled={busy}
                  data-testid={`asset-delete-${a.asset_id}`}
                  onClick={() => void library.remove(a)}
                >
                  {t("control.assets.delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pending && (
        <div
          role="alertdialog"
          data-testid="asset-delete-confirm"
          className="rounded-md border border-red-500 bg-red-950/40 p-3 text-xs space-y-2"
        >
          <p>{t("control.assets.inUse", { count: pending.usages.length })}</p>
          <ul data-testid="asset-usages" className="list-disc pl-4">
            {pending.usages.map((u, i) => (
              <li key={`${u.session_id}-${u.scene_id ?? u.preset_id ?? i}`}>
                {u.preset_id
                  ? t("control.assets.usagePreset")
                  : t("control.assets.usageScene")}
                {u.on_air_program ? ` (${t("control.assets.onAir")})` : ""}
              </li>
            ))}
          </ul>
          {onAirUse && (
            <p data-testid="asset-force-blocked" className="text-amber-300">
              {t("control.assets.forceBlocked")}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              className={btn}
              disabled={busy || onAirUse}
              data-testid="asset-delete-force"
              onClick={() => void library.remove(pending.asset, true)}
            >
              {t("control.assets.forceDelete")}
            </button>
            <button
              type="button"
              className={btn}
              data-testid="asset-delete-cancel"
              onClick={library.cancelDelete}
            >
              {t("control.assets.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
